import { Translation, QRType } from '../types';

export const en: Translation = {
  nav: {
    generator: 'Generator',
    scanner: 'Scanner',
    mobile: 'Try on Mobile',
    blog: 'Blog',
    langSelect: 'Select Language',
  },
  pageTitles: {
    generator: 'Free QR Code Generator Online',
    scanner: 'Online QR Code Scanner',
    textToQr: 'Text to QR Code Converter',
    linkToQr: 'Link to QR Code Generator',
  },
  pageMeta: {
    generator: {
      title:
        'Free QR Code Generator — Create QR Codes Online (No Signup, No Watermark, PNG/SVG/PDF) | YesQRCode',
      description:
        'Create QR codes online for free with YesQRCode. Generate URL, text, WiFi, vCard, and more. Download high-quality PNG, SVG, or PDF. No signup, no login, no watermark — fast, secure, and forever free.',
      keywords: [
        'qr code generator',
        'free qr code generator',
        'create qr code online',
        'qr code maker',
        'qr code png',
        'qr code svg',
        'qr code pdf',
        'wifi qr code generator',
        'vcard qr code generator',
        'no signup qr generator',
      ],
    },
    scanner: {
      title:
        'Online QR Code Scanner — Scan from Image or Camera (Secure, No Signup) | YesQRCode',
      description:
        'Scan QR codes and barcodes online for free. Upload an image or use your camera. All processing is done locally with no uploads. Secure, fast, no signup, no login, and completely free to use.',
      keywords: [
        'qr code scanner',
        'scan qr code online',
        'online qr reader',
        'scan qr from image',
        'scan barcode online',
        'secure qr scanner',
        'qr scanner no upload',
        'free qr scanner',
        'browser qr scanner',
        'no signup qr scanner',
      ],
    },
    textToQr: {
      title: 'Text to QR Converter | Turn Notes into QR Codes',
      description:
        'Convert text snippets, notes or messages into shareable QR codes. Apply your branding and export PNG, SVG or PDF files instantly without registration.',
      keywords: [
        'text to qr',
        'text qr generator',
        'note qr code',
        'message qr',
        'qr converter online',
        'text qr free',
      ],
    },
    linkToQr: {
      title: 'Link to QR Generator | Convert URLs into QR Codes',
      description:
        'Paste any website or campaign URL and instantly generate a branded QR code ready for print or digital use. Supports custom colors, logos and high-resolution downloads.',
      keywords: [
        'link to qr',
        'url qr generator',
        'website qr',
        'qr for link',
        'utm qr code',
        'qr download pdf',
      ],
    },
  },
  hero: {
    title: 'Create private, brand-ready QR codes — forever free.',
    subtitle: 'A fast, private, and flexible QR tool that fits any workflow.',
    desc: 'Generate and scan high-quality QR codes without accounts, watermarks, or data uploads.',
    ctaPrimary: 'Generate QR Code',
    ctaSecondary: 'Try on Mobile',
  },
  info: {
    helpTitle: 'Help Center',
    helpSubtitle: 'Frequently Asked Questions & Support',
    contactTitle: 'Contact Us',
    contactSubtitle: 'We would love to hear from you.',
    privacyTitle: 'Privacy Policy',
    termsTitle: 'Terms of Service',
  },
  contactForm: {
    name: 'Name',
    email: 'Email',
    message: 'Message',
    send: 'Send Message',
    orEmail: 'or email us directly at',
    sending: 'Sending...',
    successTitle: 'Message Sent!',
    successMessage: 'Thank you for contacting us. We will get back to you shortly.',
    sendAnother: 'Send another message',
    errorMessage: 'We could not send your message right now. Please email support@yesqrcode.com.',
  },
  generator: {
    highlights: {
      title: 'Create private, brand-ready QR codes — forever free.',
      subtitle: 'A fast, private, and flexible QR tool that fits any workflow.',
      items: [
        {
          title: 'Privacy-first, on-device generation',
          desc: 'QR codes are created locally — no uploads, no tracking, no stored data.',
        },
        {
          title: 'Rich features for every use case',
          desc: 'URLs, WiFi, vCards, barcodes, logos, gradients, frames — everything built in.',
        },
        {
          title: 'Mobile-friendly and easy for everyone',
          desc: 'Optimized for phones and touch interactions for smooth creation and scanning.',
        },
        {
          title: 'Free forever, safe for commercial use',
          desc: 'No account, no watermarks, no scan limits — reliable for any team or business.',
        },
      ],
    },
    featureSuite: {
      title: 'The complete privacy-first QR code suite',
      subtitle: 'Everything you need to create clean, branded, high-quality QR codes — all in one place.',
      categories: [
        {
          title: 'All the QR code types you need',
          body: 'URL, Text, WiFi, Email, Phone, SMS, vCard, Social links, YouTube, Barcodes. Static codes that never expire.',
          items: [],
        },
        {
          title: 'Branding & Design',
          body: 'Logo-safe codes, gradients, frames, shapes, corners, and scan-optimized layouts.',
          items: [],
        },
        {
          title: 'Output & Quality',
          body: 'High-quality PNG, SVG, and PDF exports, with presets for print and digital.',
          items: [],
        },
        {
          title: 'Global & Usability',
          body: 'Multi-language UI, mobile-first design, and offline-friendly workflow.',
          items: [],
        },
      ],
    },
    trust: {
      title: 'Trusted by creators, teams, and brands',
      subtitle:
        'YesQRCode is designed to be lightweight, predictable, and safe for long-running campaigns — with no accounts, no vendor lock-in, and no hidden limitations.',
      items: [
        'Zero onboarding',
        'Predictable static codes',
        'Enterprise-safe: no cloud processing',
        'Consistent branding across all assets',
      ],
    },
    sectionContent: 'Content',
    sectionColors: 'Colors',
    sectionLogo: 'Logo',
    sectionFrame: 'Frame',
    sectionDesign: 'Design',
    sectionCorrection: 'Error Correction',
    labelUrl: 'Website URL',
    labelText: 'Plain Text',
    labelSsid: 'Network Name (SSID)',
    labelPassword: 'Password',
    labelEncryption: 'Encryption',
    labelFgColor: 'Foreground',
    labelBgColor: 'Background',
    labelFrameText: 'Frame Phrase',
    labelFrameColor: 'Frame Color',
    placeholder: 'Enter your content here...',
    placeholders: {
      url: 'https://example.com',
      text: 'Enter text here',
      email: 'your@email.com',
      subject: 'Subject',
      message: 'Your message...',
      phone: '+1 234 567 890',
      ssid: 'MyWiFi',
      password: '***',
      barcode: '123456789012',
    },
    download: 'Download PNG',
    downloadSvg: 'Download SVG',
    generateButton: 'Generate QR Code',
    copy: 'Copy SVG',
    uploadLogo: 'Upload Image',
    preview: 'Preview',
    lowQuality: 'Low Quality',
    highQuality: 'High Quality',
    foreverFree: 'Forever Free • No Sign-up • Commercial Use',
    barcodeWarning: 'Advanced design options like Frames and Logos are not available for Barcodes.',
    barcodeColors: 'You can still customize Colors below.',
    frameStyles: {
      none: 'None',
      simple: 'Simple',
      balloon: 'Bubble',
      box: 'Box',
      badge: 'Badge',
      polaroid: 'Polaroid',
      phone: 'Phone',
      header: 'Header',
      corner: 'Corner',
    },
    vCard: {
      firstName: 'First Name',
      lastName: 'Last Name',
      mobile: 'Mobile',
      phone: 'Phone',
      fax: 'Fax',
      email: 'Email',
      company: 'Company',
      job: 'Job Title',
      street: 'Street',
      city: 'City',
      zip: 'Zip Code',
      state: 'State',
      country: 'Country',
      website: 'Website',
    },
    types: {
      [QRType.URL]: 'URL',
      [QRType.TEXT]: 'Text',
      [QRType.EMAIL]: 'Email',
      [QRType.PHONE]: 'Phone',
      [QRType.SMS]: 'SMS',
      [QRType.WIFI]: 'WiFi',
      [QRType.VCARD]: 'vCard',
      [QRType.FACEBOOK]: 'Facebook',
      [QRType.TWITTER]: 'X (Twitter)',
      [QRType.YOUTUBE]: 'YouTube',
      [QRType.BARCODE]: 'Barcode',
    },
    guide: {
      title: 'How to create a Custom QR Code?',
      steps: [],
    },
    gettingStarted: {
      title: 'How to Use',
      steps: [
        {
          title: 'Set QR Content',
          text: 'Select a content type at the top for your QR code (URL, Text, Email...). After selecting your type you will see all available options. Enter all fields that should appear when scanning your QR code. Make sure everything you enter is correct because you can’t change the content once your QR code is printed.',
        },
        {
          title: 'Customize Design',
          text: 'You want your QR code to look unique? Set a custom color and replace the standard shapes of your QR code. The corner elements and the body can be customized individually. Add a logo to your QR code. Select it from the gallery or upload your own logo image.',
        },
        {
          title: 'Generate QR Code',
          text: 'Set the pixel resolution of your QR code with the slider. Click the "Download" buttons to save your QR code. Please make sure your QR code is working correctly by scanning the preview with your QR Code scanner. Use a high resolution setting if you want to get a png code with print quality.',
        },
        {
          title: 'Download Image',
          text: 'Now you can download the image files for your QR code as .png, .svg, or .pdf. If you want a vector format with the complete design please choose .svg. SVG is working in software like Adobe Illustrator or Inkscape. The logo and design settings currently only work for .png and .svg files.',
        },
      ],
    },
    about: {
      title: 'The Free, Privacy-First QR Code Suite',
      subtitle:
        'Generate and scan high-quality QR codes without accounts, watermarks, or data uploads. Optimized for print, branding, and commercial use.',
      features: [
        'All popular types: URL, Text, WiFi, vCard, SMS, and barcodes',
        'Logo-safe with up to 30% error correction',
        'Custom shapes, gradients, colors, and frames',
        'Static QR codes that never expire',
        'Multi-language UI for global teams',
      ],
      content: [
        {
          title: 'Logo-safe QR codes',
          body: 'Add logos without worrying about scan failures. With up to 30% error correction, branded overlays stay readable for packaging, menus, and signage.',
        },
        {
          title: 'Custom shapes and frames',
          body: 'Swap corner shapes, apply gradients, and choose frames that match your campaign. Design-focused controls keep codes scannable while looking on-brand.',
        },
        {
          title: 'Multiple content types',
          body: 'Generate URL, text, WiFi, vCard, SMS, and barcode codes from one place, with presets that simplify setup for each format.',
        },
        {
          title: 'Static codes that last',
          body: 'Create static QR codes that never expire or throttle scans—ideal for long-running campaigns and printed assets.',
        },
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          q: 'What is a QR code?',
          a: 'QR code stands for Quick Response Code and is barcode type that was invented by Denso Wave in 1994.',
        },
        {
          q: 'Can I use the generated QR Codes for commercial purposes?',
          a: 'Yes, all QR codes you created with this QR generator are free and can be used for whatever you want.',
        },
        {
          q: 'Are the created qr codes expiring?',
          a: 'They do not expire and will work forever! QR Codes created with YesQRCode are static and do not stop working after a certain time. You just can’t edit the content of the QR Codes again.',
        },
        {
          q: 'Is there a scan limit for the QR codes?',
          a: 'There is no limit and the created QR code will work forever. Scan it as many times as you wish!',
        },
        {
          q: 'Is YesQRCode saving my data?',
          a: 'We do not save or reuse your data in any form. We cache your qr code image files for 24h on our server to optimize the performance of YesQRCode.',
        },
        {
          q: 'Why is my vCard QR code not showing the correct fields?',
          a: 'Not all QR code scanners follow the official vCard standard which results in mixed up contact fields. Please try another QR code scanner app for better results.',
        },
        {
          q: 'My QR code is not working, what can I do?',
          a: 'There are many reasons why a QR code is not working correctly. At first check your entered data. Sometimes there are little typos in your URL that break your QR code. Some QR codes (like vCard) contain a lot of data. Try reducing the data you entered for your QR code when possible. This can make it easier for QR code scanner apps to read your code. Try to remove the logo in your QR code and check if this helps. Also make sure that there is enough contrast between the background and foreground of the QR code. The foreground should always be darker than the background.',
        },
        {
          q: 'Does YesQRCode work in all browsers?',
          a: 'YesQRCode needs a modern HTML5 capable browser and is officially supporting Chrome, Firefox, Safari, Edge and Internet Explorer 11.',
        },
      ],
    },
  },
  scanner: {
    title: 'Scan QR & Barcodes',
    subtitle: 'Use camera, upload an image, or paste to decode QR and barcodes instantly.',
    cameraPermission: 'Camera access denied. Please enable permissions in your browser settings.',
    result: 'Result',
    copy: 'Copy',
    copied: 'Copied',
    open: 'Open Link',
    delete: 'Delete',
    scanAgain: 'Scan Another',
    privacy: 'Processing happens locally in your browser. Files never leave your device.',
    upload: 'Upload Image',
    paste: 'Paste (Ctrl+V)',
    drop: 'Drop image here',
    retry: 'Try Again',
    errorAccessDenied: 'Camera Access Denied',
    errorCamera: 'Please allow camera access in your browser settings.',
    initializing: 'Initializing Camera...',
    cameraActive: 'Camera Active',
    scannedItems: 'Scanned Items',
    recentScans: 'Recent (stored locally)',
    clickToUpload: 'Click to Upload',
    newBadge: 'NEW',
    tabCameraMobile: 'Camera',
    tabCameraDesktop: 'Scan via Camera',
    emptyState: {
      title: 'No scans yet',
      desc: 'Upload an image or use the camera tab to get started.',
    },
    highlights: {
      title: 'Fast, private scanning',
      items: [
        { title: 'On-device decoding', desc: 'No uploads by default; safe for sensitive URLs and assets.' },
        { title: 'Multi-format', desc: 'QR plus UPC/EAN, Code 128, ITF, and more.' },
        { title: 'Fast actions', desc: 'Drop files, paste screenshots, or switch to live scan instantly.' },
        { title: 'Great on mobile', desc: 'Optimized UI and camera flow for phones and small screens.' },
      ],
    },
    intro: {
      title: 'Online QR & Barcode Scanner',
      body: 'Decode QR Codes, UPC, EAN, and more right inside your browser without installing an app.',
    },
    howTo: {
      title: 'How to scan?',
      methods: [
        {
          title: 'Method 1: Upload',
          steps: ['Click Upload Image', 'Select your QR/barcode', 'Get instant result'],
        },
        {
          title: 'Method 2: Camera',
          steps: ['Open the Camera tab', 'Grant permission', 'Point to your QR/barcode'],
        },
      ],
    },
    features: {
      title: 'Why use this scanner?',
      items: [
        { title: 'Local history', desc: 'Recent scans are stored on your device only, ready to copy or open again.' },
        { title: 'Offline image decoding', desc: 'Upload or paste images to decode even when your connection drops.' },
        { title: 'Quick actions', desc: 'Copy, open, or delete results instantly with streamlined controls.' },
      ],
    },
    useCases: {
      title: 'Popular Use Cases',
      items: [
        { title: 'Retail & Shopping', desc: 'Check product URLs, coupons, or instructions.' },
        { title: 'Inventory Teams', desc: 'Verify barcodes and manage stock updates.' },
        { title: 'Everyday Users', desc: 'Quickly open menus, Wi-Fi links, or contact cards.' },
      ],
    },
    faq: {
      title: 'Scanner FAQ',
      items: [
        { q: 'Can I scan barcodes?', a: 'Yes, the scanner supports QR codes plus Code 128, UPC, EAN, ITF, and more.' },
        { q: 'Do you store my scans?', a: 'No scans leave your browser; everything stays on your device.' },
        { q: 'Can I paste screenshots?', a: 'Yes, press Ctrl+V (Cmd+V) to paste from your clipboard.' },
        { q: 'Does it work offline?', a: 'Image uploads work offline; camera scanning requires permission and a supported browser.' },
      ],
    },
  },
  blog: {
    title: 'Insights & Guides',
    subtitle: 'Best practices, marketing ideas, and product updates for QR experiences.',
    readMore: 'Read article',
    back: 'Back to blog',
    notFound: 'Blog post not found',
  },
  common: {
    footer: 'Powerful QR creation suite for marketers, designers, and teams.',
    privacy: 'Privacy',
    terms: 'Terms',
    contact: 'Contact',
    scanToOpen: 'Scan to open on mobile',
    help: 'Help',
    free: 'Free',
    product: 'Product',
    resources: 'Resources',
    company: 'Company',
    api: 'API (Coming Soon)',
    systemStatus: 'Systems Operational',
    footerDesc:
      'The most advanced QR code generator and scanner. Fast, privacy-focused, and free forever for personal and commercial use.',
  },
};
